import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItemTestId, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { ItemDetail } from '@/features/items/types/item'
import { useFileUploadField } from '@/hooks/useFileUpload'
import type { LocalizedSelectOption } from '@/types/ui'
import { type Dispatch, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { CurrentUserNote, ImageUploadField } from '../ScanItemShared'

type RepairForm = { title: string; description: string; priority: string }

export function RepairSection({
  item,
  form,
  setForm,
  priorities,
  imageField,
  currentUserName,
  isItemAvailable,
  isSubmitting,
  onSubmit,
}: {
  item: ItemDetail
  form: RepairForm
  setForm: Dispatch<SetStateAction<RepairForm>>
  priorities?: LocalizedSelectOption[]
  imageField: ReturnType<typeof useFileUploadField>
  currentUserName?: string
  isItemAvailable: boolean
  isSubmitting: boolean
  onSubmit: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-3" data-testid="repair-form">
      <div className="rounded-lg bg-secondary/30 p-3">
        <p className="text-xs text-muted-foreground">
          {t('scans.equipmentLabel')}{' '}
          <span className="font-medium text-foreground">
            {item.product?.name} ({item.serialNumber})
          </span>
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('scans.field.title')} *</Label>
          <Input
            data-testid="repair-title-input"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder={t('scans.placeholder.repairTitle')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('scans.field.priority')}</Label>
          <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
            <SelectTrigger className="h-9 bg-secondary/50 text-xs" data-testid="repair-priority-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {priorities?.map(priority => (
                <SelectItemTestId
                  key={priority.value}
                  value={priority.value}
                  testId={`repair-priority-option-${priority.value}`}
                >
                  {priority.label}
                </SelectItemTestId>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('scans.field.description')} *</Label>
        <Textarea
          data-testid="repair-description-input"
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          className="bg-secondary/50 text-xs"
          rows={3}
          placeholder={t('scans.placeholder.repairDescription')}
        />
      </div>
      <ImageUploadField
        label={t('scans.repair.imageLabel')}
        field={imageField}
        maxImages={5}
        uploadText={t('common.uploadingImages')}
        testId="repair-image-upload"
      />
      <CurrentUserNote label={t('scans.repair.reporter')} name={currentUserName} />
      <Button
        data-testid="repair-submit-button"
        onClick={onSubmit}
        className="text-xs"
        disabled={!isItemAvailable || !form.title || !form.description || isSubmitting}
      >
        {t('scans.repair.submit')}
      </Button>
    </div>
  )
}
